import type { components } from "@/api-schema";
import { useVehicleBuyInEmployeeSearchQuery } from "@/common/api/vehicle-buy-in/use-vehicle-buy-in-employee-search.query";
import { getFullUserName, UserNamePart } from "@/common/helpers/get-full-user-name";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { BaseFilterProps, Filter, FilterData, FilterPaginationState } from "../_types";
import { FilterComponent } from "../base-filter";

const FILTER_PAGE_SIZE = 50;
type Employee = Pick<components["schemas"]["User"], "firstName" | "lastName" | "email">;

function getEmployeeLabel(employee: Employee): string {
  return (
    getFullUserName({ user: employee, startingPart: UserNamePart.LastName, separator: ", " }) || employee.email || ""
  );
}

function useVehicleBuyInEmployeeFilterData(): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });
  const {
    data: users,
    isPending: isLoading,
    error,
  } = useVehicleBuyInEmployeeSearchQuery({
    options: { parameters: { pageSize, search: debouncedSearchTerm || undefined } },
  });
  const options =
    users?.data
      .map((user) => ({ id: user.uuid ?? "", name: getEmployeeLabel(user) }))
      .filter((option) => option.id && option.name) ?? [];
  const pagination = createPaginationState(users?.meta?.total ?? 0, users?.data?.length ?? 0, isLoading);

  return { data: { options, isLoading, error }, pagination };
}

export class VehicleBuyInEmployeeFilter extends Filter<Record<string, never>, unknown> {
  readonly id = "createdBy";
  readonly labelSingular = "fab:Employee";
  readonly labelPlural = "fab:Employee";
  readonly placeholder = "fab:SelectEmployee";
  readonly searchPlaceholder = "fab:SearchEmployee";
  readonly emptyText = "fab:NoEmployeeFound";
  body = {};
  parameters = {};

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps) {
    return <VehicleBuyInEmployeeFilterComponent filter={this} {...props} />;
  }
}

function VehicleBuyInEmployeeFilterComponent({
  filter,
  ...props
}: { filter: VehicleBuyInEmployeeFilter } & BaseFilterProps) {
  const { data, pagination } = useVehicleBuyInEmployeeFilterData();
  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
